import { Button } from "../ui";
import { formatSar } from "../../utils/billingMoney";
import { BRANCH_STATUS_LABEL_KEY, fillTemplate } from "./clientBillingText";

const formatDate = (value, lang = "ar") => {
  if (!value) return "-";
  return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "ar-SA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(value));
};

// manageLudModal / manageIntegrationModal: strictly read-only. Package,
// branches and dates all derive from paid invoices via the branch-tracking
// view the backend computes - there is no edit/delete affordance here by
// design (v141 §4/§Phase 4).
const ManageSubscriptionModal = ({
  subscription,
  branchTrack,
  lang,
  text,
  onClose,
  onRequestStop,
  canManage,
  branches = [],
  onAddCoverage,
  adding = "",
  error = "",
}) => {
  const isRtl = lang !== "en";
  const snapshot = subscription?.planSnapshot || {};
  // branches of the store that have no active / scheduled coverage on this subscription
  const coveredIds = new Set((branchTrack || []).filter((row) => ["active", "scheduled"].includes(row.status)).map((row) => String(row.branchId)));
  const uncovered = (branches || []).filter((branch) => !coveredIds.has(String(branch.branchId)));

  return (
    <div className="billing-modal-backdrop" dir={isRtl ? "rtl" : "ltr"}>
      <div className="billing-modal">
        <h3>{text.manageModalTitle}</h3>
        <p className="cb-modal-note">{text.manageReadonlyNote}</p>

        {subscription && (
          <div className="billing-version-groups">
            <div>
              <span>{text.currentPackageLabel}</span>
              <strong>{snapshot.code} v{snapshot.version}</strong>
            </div>
            <div>
              <span>{text.baseLabel}</span>
              <strong>{formatSar(snapshot.basePriceMinor, lang)}</strong>
            </div>
            <div>
              <span>{text.extraBranchLabel}</span>
              <strong>{formatSar(snapshot.extraBranchPriceMinor, lang)}</strong>
            </div>
            <div>
              <span>{text.colStatus}</span>
              <strong>{subscription.status}</strong>
            </div>
          </div>
        )}

        <strong>{text.branchTrackTitle}</strong>
        {!branchTrack?.length ? (
          <div className="cb-branch-track-empty">{text.branchTrackEmpty}</div>
        ) : (
          <div className="cb-table-scroll">
            <table className="cb-branch-track">
              <thead>
                <tr>
                  <th>{text.colBranch}</th>
                  <th>{text.colStart}</th>
                  <th>{text.colEnd}</th>
                  <th>{text.colRemaining}</th>
                  <th>{text.colStatus}</th>
                </tr>
              </thead>
              <tbody>
                {branchTrack.map((row) => (
                  <tr key={row.branchId}>
                    <td>{row.branchName}</td>
                    <td>{formatDate(row.startDate, lang)}</td>
                    <td>{row.endDate ? formatDate(row.endDate, lang) : "-"}</td>
                    <td>
                      {row.remainingDays != null
                        ? fillTemplate(text.remainingDaysTemplate, { days: row.remainingDays })
                        : text.remainingOpenEnded}
                    </td>
                    <td>{text[BRANCH_STATUS_LABEL_KEY[row.status]] || row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {canManage && subscription?.status === "active" && onAddCoverage && (
          <>
            <strong>{text.uncoveredTitle}</strong>
            {error && <div className="billing-error" role="alert">{error}</div>}
            {uncovered.length === 0 ? (
              <div className="cb-branch-track-empty">{text.uncoveredNone}</div>
            ) : (
              <>
                <p className="cb-modal-note">{text.addCoverageNote}</p>
                <ul className="cb-uncovered">
                  {uncovered.map((branch) => (
                    <li key={branch.branchId}>
                      <span dir="auto">{branch.name}</span>
                      <Button size="sm" appearance="outline" disabled={Boolean(adding)} onClick={() => onAddCoverage(String(branch.branchId))}>
                        {adding === String(branch.branchId) ? "…" : text.addCoverageCta}
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        <div className="billing-modal-actions">
          {canManage && subscription?.status === "active" && (
            <Button appearance="outline" variant="danger" onClick={onRequestStop}>
              {text.stopCta}
            </Button>
          )}
          <Button appearance="ghost" onClick={onClose}>
            {text.close}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManageSubscriptionModal;
